import * as Sentry from "@sentry/react";
import { API_URL } from "./constants";
import { recordOperationalAction, recordUiError } from "./observability";

// Fluxos de negócio esperados (409 de conferência SGT) não são erros e o
// corpo carrega pedidos/passageiros — não podem ir ao Sentry.
const EXPECTED_API_CODES = new Set([
  "DIVERGENCIA_SGT",
  "CONFERENCIA_SGT_INDISPONIVEL",
  "ROUTE_COMMUNICATION_PENDING",
  "GOOGLE_DIRECTIONS_UNAVAILABLE",
]);

const shouldReportToSentry = (endpoint, status) => {
  if (endpoint.includes("/auth/login")) return false;
  // Erros HTTP 4xx representam validações/regras de negócio e já são
  // apresentados ao operador. O Sentry do navegador deve registrar falha de
  // rede ou resposta 5xx inesperada, sem transformar duplo clique, conflito e
  // rate limit em incidente de aplicação.
  if (status == null) return true;
  return Number(status) >= 500;
};

const normalizeEndpoint = (endpoint) => String(endpoint || "")
  .split("?")[0]
  .replace(/\/\d+(?=\/|$)/g, "/:id")
  .slice(0, 180);

const reportApiError = (error, { endpoint, method, status, errorCode, kind, durationMs }) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  const safeEndpoint = normalizeEndpoint(endpoint);
  Sentry.withScope((scope) => {
    scope.setTag("api_endpoint", safeEndpoint);
    scope.setTag("api_kind", kind);
    if (method) scope.setTag("http_method", method);
    if (status != null) scope.setTag("http_status", String(status));
    scope.setContext("api_request", {
      endpoint: safeEndpoint,
      method,
      status: status ?? null,
      error_code: errorCode || null,
      duration_ms: durationMs ?? null,
      online: typeof navigator === "undefined" ? null : navigator.onLine,
      visibility: typeof document === "undefined" ? null : document.visibilityState,
    });
    Sentry.captureException(error);
  });

  error._sentryApiReported = true;
};

export const apiFetch = async (endpoint, options = {}) => {
  const method = (options.method || "GET").toUpperCase();
  const safeEndpoint = normalizeEndpoint(endpoint);
  const startedAt = Date.now();
  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    Accept: "application/json",
    ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  if (method !== "GET") {
    recordOperationalAction("api_mutation_started", {
      endpoint: safeEndpoint,
      http_method: method,
    });
  }

  let res;
  try {
    res = await fetch(`${API_URL}${endpoint}`, { ...options, headers, credentials: "include" });
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    const durationMs = Date.now() - startedAt;
    recordUiError({
      message: err.message,
      code: "network_failure",
      source: "api",
      emitEvent: false,
      details: {
        endpoint: safeEndpoint,
        http_method: method,
        duration_ms: durationMs,
        online: typeof navigator === "undefined" ? false : navigator.onLine,
        visibility: typeof document === "undefined" ? "unknown" : document.visibilityState,
      },
    });
    if (shouldReportToSentry(endpoint)) {
      reportApiError(err, { endpoint, method, kind: "network", durationMs });
    }
    throw err;
  }

  if (res.status === 401 && !endpoint.includes("/auth/login") && !endpoint.includes("/auth/me")) {
    const isMutation = method !== "GET";
    const err = new Error("Sessão expirada");
    err.status = 401;
    err.code = isMutation ? "session_expired_during_mutation" : "session_expired";
    recordUiError({
      message: err.message,
      code: err.code,
      source: "api",
      emitEvent: isMutation,
      details: {
        endpoint: safeEndpoint,
        http_method: method,
        http_status: 401,
        duration_ms: Date.now() - startedAt,
      },
    });
    localStorage.removeItem("rotaflex_token");
    // Dá tempo para o evento operacional e o buffer de replay serem iniciados
    // antes de desmontar a página. A renovação deslizante evita este caminho
    // para operadores ativos; ele permanece como proteção de sessão inválida.
    window.dispatchEvent(new CustomEvent("rotaflex-session-expired"));
    throw err;
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const detalhe = data.detalhe ? String(data.detalhe).trim() : "";
    const err = new Error(
      detalhe && detalhe !== data.erro
        ? `${data.erro || "Erro na comunicação com o servidor."} (${detalhe})`
        : (data.erro || "Erro na comunicação com o servidor.")
    );
    err.status = res.status;
    err.code = data.code || data.status || null;
    if (data.falhas) err.falhas = data.falhas;
    if (data.rota_pendente) err.rota_pendente = data.rota_pendente;
    if (data.motorista_nome) err.motorista_nome = data.motorista_nome;
    if (data.conferencias) err.conferencias = data.conferencias;
    if (data.snapshot) err.snapshot = data.snapshot;
    if (data.ultima_tentativa) err.ultima_tentativa = data.ultima_tentativa;
    if (data.motivo) err.motivo = data.motivo;
    if (data.grupo_index !== undefined) err.grupo_index = data.grupo_index;
    if (data.grupo_label) err.grupo_label = data.grupo_label;
    if (data.resumo_comunicacao) err.resumo_comunicacao = data.resumo_comunicacao;
    const durationMs = Date.now() - startedAt;
    if (!endpoint.includes("/auth/login")) {
      recordUiError({
        message: err.message,
        code: err.code || `http_${res.status}`,
        source: "api",
        emitEvent: false,
        details: {
          endpoint: safeEndpoint,
          http_method: method,
          http_status: res.status,
          error_code: err.code || `HTTP_${res.status}`,
          duration_ms: durationMs,
        },
      });
    }
    if (shouldReportToSentry(endpoint, res.status) && !EXPECTED_API_CODES.has(err.code)) {
      reportApiError(err, {
        endpoint,
        method,
        status: res.status,
        errorCode: err.code,
        kind: "http",
        durationMs,
      });
    }
    throw err;
  }

  if (method !== "GET") {
    recordOperationalAction("api_mutation_succeeded", {
      endpoint: safeEndpoint,
      http_method: method,
      http_status: res.status,
      duration_ms: Date.now() - startedAt,
    });
  }
  return data;
};

export const apiDownload = async (endpoint) => {
  const safeEndpoint = normalizeEndpoint(endpoint);
  const startedAt = Date.now();
  const res = await fetch(`${API_URL}${endpoint}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/csv" },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data.erro || "Erro ao exportar os dados.");
    recordUiError({
      message: error.message,
      code: data.code || `http_${res.status}`,
      source: "api_download",
      emitEvent: false,
      details: {
        endpoint: safeEndpoint,
        http_method: "GET",
        http_status: res.status,
        duration_ms: Date.now() - startedAt,
      },
    });
    throw error;
  }
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/i);
  return {
    blob: await res.blob(),
    filename: match?.[1] || "contingencia.csv",
  };
};
